import React, { useCallback, useMemo } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { FaArrowLeft, FaPen } from "react-icons/fa";

import { EmptyState } from "components/admin";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { SchoolOpsHeader } from "context/school-ops-header-context";
import { useAuth } from "context/auth-context";
import { useSchoolTeamIdentity } from "pages/app/workspace/school-team-profile/hooks/use-school-team-identity";
import {
  TEAM_PROFILE_TABS,
  TeamProfileTab,
  parseTeamProfileTab,
} from "pages/app/workspace/school-team-profile/school-team-profile.types";
import { SchoolTeamActivityTab } from "pages/app/workspace/school-team-profile/tabs/activity-tab";
import { SchoolTeamDocumentsTab } from "pages/app/workspace/school-team-profile/tabs/documents-tab";
import { SchoolTeamMediaTab } from "pages/app/workspace/school-team-profile/tabs/media-tab";
import { SchoolTeamOverviewTab } from "pages/app/workspace/school-team-profile/tabs/overview-tab";
import { SchoolTeamRosterTab } from "pages/app/workspace/school-team-profile/tabs/roster-tab";
import { SchoolTeamScheduleTab } from "pages/app/workspace/school-team-profile/tabs/schedule-tab";
import {
  SchoolTeamActivityTabSkeleton,
  SchoolTeamOverviewTabSkeleton,
  SchoolTeamProfileTabSkeleton,
  SchoolTeamRosterTabSkeleton,
} from "pages/app/workspace/school-team-profile.skeleton";
import {
  SchoolHubContentStack,
  SchoolHubTabButton,
  SchoolHubTabRow,
} from "pages/app/workspace/styles/school-hub.page.inline.styles";
import {
  SchoolTeamProfilePrimaryButton,
  SchoolTeamProfileSecondaryButton,
  SchoolTeamProfileSection,
} from "pages/app/workspace/styles/school-team-profile.page.inline.styles";
import { hasAnyPortalAction } from "utils/portal-access";

export type { TeamProfileTab } from "pages/app/workspace/school-team-profile/school-team-profile.types";

const SchoolTeamProfilePage: React.FC = () => {
  const { schoolId, id } = useParams<{
    schoolId: string;
    id: string;
  }>();
  const history = useHistory();
  const location = useLocation<{
    backTo?: string;
  }>();
  const { portalAccess } = useAuth();
  const { isLoading, resolvedSchoolId, team } = useSchoolTeamIdentity(id, schoolId);

  const activeTab = useMemo(() => parseTeamProfileTab(location.search), [location.search]);
  const backTo = location.state?.backTo || "/workspace/school-teams";
  const profileBackTo = `${location.pathname}${location.search}`;
  const canEditTeams = hasAnyPortalAction(portalAccess, [
    "school_teams.update",
    "school_teams.assign_coach",
    "school_teams.manage",
  ]);

  const loadingSkeleton = useMemo(() => {
    if (activeTab === "overview") {
      return <SchoolTeamOverviewTabSkeleton />;
    }

    if (activeTab === "roster") {
      return <SchoolTeamRosterTabSkeleton />;
    }

    if (activeTab === "activity") {
      return <SchoolTeamActivityTabSkeleton />;
    }

    return <SchoolTeamProfileTabSkeleton />;
  }, [activeTab]);

  const setTab = useCallback(
    (tab: TeamProfileTab) => {
      history.replace({
        pathname: location.pathname,
        search: tab === "overview" ? "" : `?tab=${tab}`,
        state: location.state,
      });
    },
    [history, location.pathname, location.state],
  );

  const headerActions = useMemo(
    () => (
      <>
        <SchoolTeamProfileSecondaryButton onClick={() => history.push(backTo)} type="button">
          <FaArrowLeft size={12} />
          Back
        </SchoolTeamProfileSecondaryButton>
        {canEditTeams ? (
          <SchoolTeamProfilePrimaryButton
            onClick={() => history.push("/workspace/school-teams")}
            type="button"
          >
            <FaPen size={12} />
            Manage team
          </SchoolTeamProfilePrimaryButton>
        ) : null}
      </>
    ),
    [backTo, canEditTeams, history],
  );

  const headerTabs = useMemo(
    () =>
      team ? (
        <SchoolHubTabRow>
          {TEAM_PROFILE_TABS.map((tab) => (
            <SchoolHubTabButton
              $active={activeTab === tab.id}
              key={tab.id}
              onClick={() => setTab(tab.id)}
              type="button"
            >
              {tab.label}
            </SchoolHubTabButton>
          ))}
        </SchoolHubTabRow>
      ) : undefined,
    [activeTab, setTab, team],
  );

  const tabProps = {
    profileBackTo,
    schoolId: resolvedSchoolId,
    team,
    teamId: id,
  };

  return (
    <AppLayout route="/workspace/school-teams">
      <SchoolOpsHeader actions={headerActions} tabs={headerTabs} title={team?.name || "School team"} />

      <SchoolHubContentStack>
        {isLoading ? loadingSkeleton : null}

        {!isLoading && !team ? (
          <SchoolTeamProfileSection>
            <EmptyState
              description="This team could not be loaded from the current school scope."
              title="Team not found"
            />
          </SchoolTeamProfileSection>
        ) : null}

        {team && activeTab === "overview" ? <SchoolTeamOverviewTab {...tabProps} /> : null}
        {team && activeTab === "schedule" ? <SchoolTeamScheduleTab {...tabProps} /> : null}
        {team && activeTab === "roster" ? <SchoolTeamRosterTab {...tabProps} /> : null}
        {team && activeTab === "activity" ? <SchoolTeamActivityTab {...tabProps} /> : null}
        {team && activeTab === "media" ? <SchoolTeamMediaTab {...tabProps} /> : null}
        {team && activeTab === "documents" ? <SchoolTeamDocumentsTab {...tabProps} /> : null}
      </SchoolHubContentStack>
    </AppLayout>
  );
};

export default SchoolTeamProfilePage;
